import { Appointment, VendorMetaType } from "@elixir/types";
import {
  getDateFromTimeInMillisWithFallback,
  timestampToIsoString,
} from "../../../firestore/utils";
import { getAppointmentAttributes } from "./get-apt-attributes";
import { getTeleLinks } from "../../../apis/tryst/get-all-queues";
import {
  LocalPatient,
  PatientDetailsWithMeta,
} from "../../../utils/bridges/types/patient";
import {
  FirebaseAppointment,
  OfflineAppointment,
  FirebaseAppointmentMeta,
} from "../../../firestore/types";

export function firebaseAptToAppointment(
  appointment: FirebaseAppointment,
  patient: Appointment["patient"],
  formData: Appointment["formData"]
): Appointment {
  const {
    clinic_id,
    receipts,
    status,
    call_status,
    callback_requested,
    channel,
    mode,
    payment_id,
    payment_status,
    prescription_id,
    prescription_url,
    visit_start,
  } = appointment;
  return {
    ...appointment,
    date: timestampToIsoString(appointment.date)!,
    full_date: timestampToIsoString(appointment.full_date)!,
    start_time: timestampToIsoString(appointment.start_time)!,
    end_time: timestampToIsoString(appointment.end_time)!,
    visit_start: timestampToIsoString(appointment.visit_start),
    visit_end: timestampToIsoString(appointment.visit_end),
    last_visit_time: timestampToIsoString(appointment.last_visit_time),
    callback_requested: timestampToIsoString(appointment.callback_requested),
    created_at: timestampToIsoString(appointment.created_at),
    updated_at: timestampToIsoString(appointment.updated_at),
    follow_up: timestampToIsoString(appointment.follow_up),
    visit_checkin: timestampToIsoString(appointment.visit_checkin),
    ...getAppointmentAttributes({
      clinic_id,
      receipts,
      status,
      call_status,
      callback_requested: callback_requested?.toMillis(),
      channel,
      mode,
      payment_id,
      payment_status,
      prescription_id,
      prescription_url,
      visit_start: visit_start?.toMillis(),
    }),
    patient,
    formData,
    vendor_meta: [],
    vc_meta: getTeleLinks({ appointment }),
  };
}

export function convertLocalPatientToAuthPatient(
  patient: LocalPatient
): Appointment["patient"] {
  const gender = (patient?.personal?.gender || "unknown")[0].toUpperCase();
  const mobile = `${patient?.personal?.phone?.c || ""}${
    patient?.personal?.phone?.n || ""
  }`;

  const createdAtMs = patient?.createdAt
    ? new Date(patient.createdAt).getTime()
    : NaN;
  return {
    at: !isNaN(createdAtMs)
      ? new Date(createdAtMs).toISOString()
      : new Date().toISOString(),
    "dob-valid": true,
    "health-ids": patient?.personal["health-ids"] || [],
    "is-p": true,
    fn: patient?.personal?.name?.f || "",
    ln: patient?.personal.name.l || "",
    fln: `${patient?.personal.name.f || ""} ${
      patient?.personal.name.l || ""
    }`.trim(),
    amount_pending: patient.payments?.dues ? patient.payments.dues : 0,
    gen: gender,
    mn: "",
    uuid: patient?.uuid || "",
    mobile,
    oid: patient?.id || patient?.localId || "",
    dob: patient?.personal.age?.dob || null,
    email: null,
    pic: null,
    type: 3,
  };
}

export function offlineAptToAppointment(
  apt: OfflineAppointment,
  patient: LocalPatient,
  visitMeta: PatientDetailsWithMeta["visits_meta"] = { visitCount: 0 }
): Appointment {
  return {
    ...apt,
    ...getAppointmentAttributes(apt),
    date: getDateFromTimeInMillisWithFallback(apt.date)!,
    full_date: getDateFromTimeInMillisWithFallback(apt.full_date)!,
    start_time: getDateFromTimeInMillisWithFallback(apt.start_time)!,
    end_time: getDateFromTimeInMillisWithFallback(apt.end_time)!,
    follow_up: getDateFromTimeInMillisWithFallback(apt.follow_up),
    created_at: getDateFromTimeInMillisWithFallback(apt.created_at),
    updated_at: getDateFromTimeInMillisWithFallback(apt.updated_at),
    visit_checkin: getDateFromTimeInMillisWithFallback(apt.visit_checkin),
    visit_start: getDateFromTimeInMillisWithFallback(apt.visit_start),
    visit_end: getDateFromTimeInMillisWithFallback(apt.visit_end),
    callback_requested: getDateFromTimeInMillisWithFallback(
      apt.callback_requested
    ),
    vendor_meta: formatAppointmentVendorMeta(apt),
    vc_meta: getTeleLinks({ appointment: apt }),
    patient: convertLocalPatientToAuthPatient(patient),
    formData: patient.formData || [],
    visit_count: visitMeta.visitCount,
    last_visit_time: visitMeta.lastVisitDate
      ? visitMeta.lastVisitDate.toISOString()
      : getDateFromTimeInMillisWithFallback(apt.last_visit_time),
    abha: patient.abha,
    assessments: apt.assessments,
  };
}

function formatAppointmentVendorMeta(
  firebaseAppointmentMeta: FirebaseAppointmentMeta
) {
  const { client_payment_id, client_pt_id, client_visit_id, meta_data } =
    firebaseAppointmentMeta;
  const vendorMeta: Appointment["vendor_meta"] = [];

  if (client_payment_id) {
    vendorMeta.push({
      id: "client_payment_id",
      name: "Vendor Payment ID",
      value: client_payment_id,
      type: VendorMetaType.TEXT,
    });
  }

  if (client_pt_id) {
    vendorMeta.push({
      id: "client_pt_id",
      name: "Vendor Patient ID",
      value: client_pt_id,
      type: VendorMetaType.TEXT,
    });
  }
  if (client_visit_id) {
    vendorMeta.push({
      id: "client_visit_id",
      name: "Vendor Visit ID",
      value: client_visit_id,
      type: VendorMetaType.TEXT,
    });
  }

  if (meta_data) {
    let parsedMetadata = meta_data;
    if (typeof meta_data === "string") {
      parsedMetadata = JSON.parse(meta_data);
    }

    for (const [key, value] of Object.entries(parsedMetadata)) {
      if (value) {
        vendorMeta.push({
          id: key,
          name: key.replace(/_/g, " "),
          value: (value as string | number).toString(),
          type: key.endsWith("link")
            ? VendorMetaType.LINK
            : VendorMetaType.TEXT,
        });
      }
    }
  }

  return vendorMeta;
}
